import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
import { useParams } from "react-router";
import { CsvParseResult } from "@bn/api/apiv3";
import { useAccountsId } from "@bn/api/apiv1";
import { PageLayout } from "@components";
import { SelectFileAndFormat } from "@features/csvDrop/SelectFileAndFormat";
import { isRowBlocked } from "@features/csvDrop/helpers";
import { ParsingResults } from "./ParsingResults/ParsingResults";

const hasRowErrors = (parsingResult: CsvParseResult | null): boolean => {
  const type = parsingResult?.type;
  if (!type) return false;
  return !!parsingResult?.result?.some((row) => isRowBlocked(row, type));
};

export const CsvUploadPage = () => {
  const { t } = useTranslation();
  const [step, setStep] = useState<number>(0);
  const [parsingResult, setParsingResult] = useState<CsvParseResult | null>(
    null
  );
  const [hadCorrectionStep, setHadCorrectionStep] = useState(false);
  const { depotId } = useParams();

  const { data: depot, isFetching: idDepotLoading } = useAccountsId(
    {
      id: Number(depotId),
    },
    {
      enabled: Boolean(depotId),
    }
  );

  const breadcrumbs: BreadcrumbItem[] = depotId
    ? [
        {
          text: t("breadcrumbs.home"),
          to: "/",
        },
        {
          text: t("breadcrumbs.depotsAccounts"),
          to: "/depots-accounts",
        },
        {
          to: `/depots/${depotId}`,
          text: depot?.name || t("breadcrumbs.depot"),
          isFetching: idDepotLoading,
        },
        {
          text: t("csvImport.title"),
        },
      ]
    : [
        {
          text: t("breadcrumbs.home"),
          to: "/",
        },
        {
          text: t("breadcrumbs.investments"),
          to: "/investments",
        },
        {
          text: t("csvImport.title"),
        },
      ];

  const errorMessage = useMemo(() => {
    if (
      (depot?.type === "01_depot" &&
        parsingResult?.type === "cash_transaction") ||
      (depot?.type === "02_cash" && parsingResult?.type === "booking")
    ) {
      return t(`csvImport.csvUpload.incompatible.${depot?.type}`);
    }
    return undefined;
  }, [depot?.type, parsingResult?.type, t]);

  return (
    <PageLayout title={t("csvImport.title")} breadcrumbs={breadcrumbs}>
      <Box display={step === 0 ? "block" : "none"}>
        <SelectFileAndFormat
          errorMessage={errorMessage}
          onCsvUpload={setParsingResult}
          changeStep={() => {
            const needsCorrection = hasRowErrors(parsingResult);
            setHadCorrectionStep(needsCorrection);
            setStep(needsCorrection ? 1 : 2);
          }}
        />
      </Box>
      {parsingResult && depotId && (
        <Box display={step >= 1 ? "block" : "none"}>
          <ParsingResults
            parsingResult={parsingResult}
            changeStep={setStep}
            depotId={Number(depotId)}
            type={parsingResult.type!}
            showOnlyErrors={step === 1}
            hadCorrectionStep={hadCorrectionStep}
          />
        </Box>
      )}
    </PageLayout>
  );
};
